import { useCallback, useEffect, useMemo, useState } from 'react';
import AiTermsGate from '../ai/AiTermsGate';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { token as liveToken } from '../auth/session';
import { useAiAccess, refreshAiAccess } from '../ai/useAiAccess';
import CreditsBar from '../ai/CreditsBar';
import WhereButton from '../ai/WhereButton';
import { useLearner } from '../learners/LearnerContext';
import { useAuth } from '../auth/AuthContext';
import { listStudioTypes, type StudioManifest, type StudioType } from '../studio/types';
import StudioPreview from '../studio/StudioPreview';
import StudioFields from '../studio/StudioFields';
import type { PreparedPhoto } from '../studio/photo';
import { CONCEPTS } from '../concepts/registry';

type RefGame = { ref: string; name: string; group: string };

const MAX_REFS = 5;
const VAGUE = [/\b(?:my|our)\s+(?:own\s+)?(?:[\w'’-]+\s+){0,3}?(?:game|program|activity)s?\b/i, /\blike\s+(?:my|our)\b/i];

function namedIn(text: string, games: RefGame[]): RefGame[] {
  const lower = text.toLowerCase();
  return games.filter((g) => {
    const n = g.name.toLowerCase().trim();
    if (n.length < 4) return false;
    return new RegExp(`(^|[^\\p{L}\\p{N}])${n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}($|[^\\p{L}\\p{N}])`, 'u').test(lower);
  });
}

function ReferencePicker({ games, chosen, onChange }: { games: RefGame[]; chosen: string[]; onChange: (refs: string[]) => void }) {
  const [filter, setFilter] = useState('');
  const shown = games.filter((g) => !filter.trim() || `${g.name} ${g.group}`.toLowerCase().includes(filter.trim().toLowerCase()))
    .sort((a, b) => Number(chosen.includes(b.ref)) - Number(chosen.includes(a.ref)));
  const full = chosen.length >= MAX_REFS;
  return (
    <fieldset className="sub-group">
      <legend className="field-label">Games to use as reference</legend>
      <p className="field-hint" style={{ marginTop: 0 }}>Choose up to {MAX_REFS} games for Studio to learn from, for example to make a game like one of yours with a change. {chosen.length} chosen.</p>
      <div className="field" style={{ margin: 0 }}>
        <input aria-label="Find a game" value={filter} placeholder="Find a game" onChange={(e) => setFilter(e.target.value)} />
      </div>
      <div className="studio-refs">
        {shown.map((g) => {
          const on = chosen.includes(g.ref);
          return (
            <label key={g.ref} className={`studio-ref${on ? ' is-selected' : ''}`}>
              <input type="checkbox" checked={on} disabled={!on && full}
                onChange={() => onChange(on ? chosen.filter((r) => r !== g.ref) : [...chosen, g.ref])} />
              <span>{g.name}</span>
              <span className="muted">{g.group}</span>
            </label>
          );
        })}
        {shown.length === 0 && <p className="field-hint">No game matches.</p>}
      </div>
    </fieldset>
  );
}

type Generation = {
  id: string;
  status: 'queued' | 'running' | 'stopping' | 'waiting' | 'done' | 'declined' | 'failed';
  progress: string;
  brief: string;
  createdAt: string;
  finishedAt: string | null;
  credits: number;
  activeSeconds: number;
  summary: string | null;
  error: string | null;
  manifest: StudioManifest | null;
  code: string | null;
  sample: Record<string, unknown> | null;
  checks: { passed: boolean; checks: { id: string; ok: boolean; detail: string }[] } | null;
  screenshots: string[];
  savedTypeId: string | null;
  revisionOf: string | null;
  updatesType: string | null;
  chat: { from: 'adult' | 'studio'; text: string; at: string }[];
};

const SHOT_NAMES: Record<string, string> = {
  '1-start': 'First round', '2-after-right': 'After a right answer', '3-after-wrong': 'After a wrong answer',
  '4-phone': 'On a phone', '5-phone-after-right': 'On a phone, after a right answer', '6-errorless': 'Errorless',
};

function Screenshot({ generationId, path }: { generationId: string; path: string }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let revoked: string | null = null;
    let cancelled = false;
    void (async () => {
      const t = await liveToken();
      const res = await fetch(`/api/studio/generations/${generationId}/files/${path}`, { headers: t ? { Authorization: `Bearer ${t}` } : {} });
      if (!res.ok || cancelled) return;
      revoked = URL.createObjectURL(await res.blob());
      setUrl(revoked);
    })();
    return () => { cancelled = true; if (revoked) URL.revokeObjectURL(revoked); };
  }, [generationId, path]);
  const key = path.replace(/^checks\//, '').replace(/\.png$/, '');
  return (
    <figure className="studio-shot">
      {url ? <img src={url} alt={SHOT_NAMES[key] ?? key} /> : <div className="studio-shot-empty" />}
      <figcaption>{SHOT_NAMES[key] ?? key}</figcaption>
    </figure>
  );
}

type Clean = (text: string) => { text: string; removed: number };

function Revise({ gen, clean, blocked, onStarted }: { gen: Generation; clean: Clean; blocked: boolean; onStarted: (id: string) => void }) {
  const [changes, setChanges] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cleaned = clean(changes);

  async function send() {
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ id: string }>(`/studio/generations/${gen.id}/revise`, { method: 'POST', token: true, body: { changes: changes.trim() } });
      setChanges('');
      onStarted(r.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not send the changes.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="studio-revise">
      <h4 style={{ margin: 0 }}>Ask for changes</h4>
      <div className="field">
        <label htmlFor={`studio-changes-${gen.id}`}>What should be different</label>
        <textarea id={`studio-changes-${gen.id}`} rows={4} maxLength={2000} value={changes}
          placeholder="e.g. Make the coins bigger. Show the number as a word as well. Only three coins on screen."
          onChange={(e) => setChanges(e.target.value)} />
        <p className="field-hint">This conversation has finished. Studio starts a new one from this game and changes only what you ask for.</p>
      </div>
      {changes.trim() && (
        <div className="studio-sent">
          <p className="field-label" style={{ marginBottom: 4 }}>This is exactly what would be sent, with this game</p>
          <pre className="studio-sent-text">{cleaned.text}</pre>
          {cleaned.removed > 0 && <p className="field-hint">{cleaned.removed} personal detail{cleaned.removed === 1 ? ' was' : 's were'} removed.</p>}
        </div>
      )}
      <div className="form-actions">
        <button type="button" className="btn" disabled={busy || blocked || !changes.trim()} onClick={() => void send()}>Send changes</button>
      </div>
      {blocked && <p className="field-hint">A game is being made. You can ask for changes when it is finished.</p>}
      {error && <p className="error" role="alert">{error}</p>}
    </div>
  );
}

function ChatLines({ gen }: { gen: Generation }) {
  const lines = [{ from: 'adult' as const, text: gen.brief, at: gen.createdAt }, ...gen.chat];
  return (
    <ol className="studio-chat-lines">
      {lines.map((l, i) => (
        <li key={i} className={`studio-chat-line is-${l.from}`}>
          <span className="studio-chat-who">{l.from === 'adult' ? 'You' : 'Studio'}</span>
          <p className="studio-sent-text">{l.text}</p>
        </li>
      ))}
    </ol>
  );
}

function Conversation({ gen, clean, blocked, onChanged }: { gen: Generation; clean: Clean; blocked: boolean; onChanged: (g: Generation) => void }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cleaned = clean(text);
  const open = gen.status === 'waiting';

  async function send() {
    setBusy(true);
    setError(null);
    try {
      onChanged(await api<Generation>(`/studio/generations/${gen.id}/messages`, { method: 'POST', token: true, body: { text: text.trim() } }));
      setText('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not send your message.');
    } finally {
      setBusy(false);
    }
  }

  async function finish() {
    setBusy(true);
    setClosing(true);
    setError(null);
    try {
      onChanged(await api<Generation>(`/studio/generations/${gen.id}/finish`, { method: 'POST', token: true }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not finish the conversation.');
    } finally {
      setBusy(false);
      setClosing(false);
    }
  }

  if (!open && !error) return null;
  return (
    <div className="studio-chat">
      {open && (
        <>
          <div className="field">
            <label htmlFor={`studio-reply-${gen.id}`}>Reply to Studio</label>
            <textarea id={`studio-reply-${gen.id}`} rows={3} maxLength={2000} value={text}
              placeholder="e.g. Make the pictures bigger. Use only three at a time."
              onChange={(e) => setText(e.target.value)} />
            <p className="field-hint">Studio remembers this conversation until you press Finish, or for 24 hours after the last message.</p>
          </div>
          {text.trim() && (
            <div className="studio-sent">
              <p className="field-label" style={{ marginBottom: 4 }}>This is exactly what would be sent</p>
              <pre className="studio-sent-text">{cleaned.text}</pre>
              {cleaned.removed > 0 && <p className="field-hint">{cleaned.removed} personal detail{cleaned.removed === 1 ? ' was' : 's were'} removed.</p>}
            </div>
          )}
          <div className="form-actions">
            <button type="button" className="btn" disabled={busy || blocked || !text.trim()} onClick={() => void send()}>Send</button>
            <button type="button" className="btn secondary" disabled={busy} onClick={() => void finish()}>{closing ? 'Finishing…' : 'Finish – I am happy with it'}</button>
          </div>
          {blocked && <p className="field-hint">Another game is being made. You can reply when it is finished.</p>}
        </>
      )}
      {error && <p className="error" role="alert">{error}</p>}
    </div>
  );
}

function GenerationCard({ gen, onSaved, clean, blocked, onRevised, onChanged, parentName }: { gen: Generation; onSaved: () => void; clean: Clean; blocked: boolean; onRevised: (id: string) => void; onChanged: (g: Generation) => void; parentName: string | null }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState(String(gen.manifest?.name ?? ''));
  const active = gen.status === 'queued' || gen.status === 'running';
  const failing = gen.checks?.checks.filter((c) => !c.ok) ?? [];

  async function save() {
    if (!gen.updatesType && !name.trim()) { setError('Give the game a name.'); return; }
    setBusy(true);
    setError(null);
    try {
      await api(`/studio/generations/${gen.id}/save`, { method: 'POST', token: true, body: gen.updatesType ? {} : { name: name.trim() } });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save it.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="studio-gen">
      <div className="studio-type-head">
        <h3 style={{ margin: 0 }}>{gen.manifest?.name ?? (active ? 'Making your game' : 'Game request')}</h3>
        <span className="muted">{new Date(gen.createdAt).toLocaleString()}</span>
      </div>
      {gen.revisionOf && <p className="field-label" style={{ margin: 0 }}>{parentName ? `Changes to ${parentName}` : 'Changes asked for'}</p>}
      <ChatLines gen={gen} />
      {active && (
        <p role="status">{gen.progress || 'Waiting to start'}. {gen.chat.some((l) => l.from === 'adult') ? 'A reply usually takes a few minutes.' : 'This usually takes 10 to 20 minutes.'} You can leave this page and come back.</p>
      )}
      {gen.summary && gen.code && <p><strong>About this game:</strong> {gen.summary}</p>}
      {gen.summary && !gen.code && gen.chat.length === 0 && <p>{gen.summary}</p>}
      {gen.error && <p className="error" role="alert">{gen.error}</p>}
      {(gen.status === 'done' || gen.status === 'waiting') && gen.manifest && gen.code && (
        <>
          <p className="muted">{gen.checks?.passed ? `Passed all ${gen.checks.checks.length} automatic checks.` : `${failing.length} automatic check${failing.length === 1 ? '' : 's'} did not pass.`}</p>
          {failing.length > 0 && (
            <ul className="studio-failing">
              {failing.map((c) => <li key={c.id}><strong>{c.id}</strong>{c.detail ? `: ${c.detail}` : ''}</li>)}
            </ul>
          )}
          <StudioPreview manifest={gen.manifest} code={gen.code} sample={gen.sample ?? {}} />
          {gen.screenshots.length > 0 && (
            <details>
              <summary className="studio-data-summary">Pictures from the checks</summary>
              <div className="studio-shots">
                {gen.screenshots.map((s) => <Screenshot key={s} generationId={gen.id} path={s} />)}
              </div>
            </details>
          )}
          <div className="form-actions">
            {gen.savedTypeId
              ? <span className="muted">Saved. It is in Studio game types below.</span>
              : gen.updatesType
              ? <>
                  <p className="field-hint" style={{ margin: 0, flex: '1 1 18rem' }}>Saving replaces your saved game “{gen.updatesType}”. Programs made from it use this version from now on.</p>
                  <button type="button" className="btn" disabled={busy} onClick={() => void save()}>Save changes</button>
                </>
              : <>
                  <div className="field" style={{ margin: 0, flex: '1 1 18rem' }}>
                    <label htmlFor={`studio-name-${gen.id}`}>Name</label>
                    <input id={`studio-name-${gen.id}`} value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />
                  </div>
                  <button type="button" className="btn" disabled={busy || !name.trim()} onClick={() => void save()}>Save as a game type</button>
                </>}
          </div>
          {error && <p className="error" role="alert">{error}</p>}
          {gen.status === 'done' && <Revise gen={gen} clean={clean} blocked={blocked} onStarted={onRevised} />}
        </>
      )}
      <Conversation gen={gen} clean={clean} blocked={blocked} onChanged={onChanged} />
      {!active && <WhereButton url={`/studio/generations/${gen.id}/flow`} />}
      {!active && <p className="field-hint">Used {gen.credits} credits · {Math.round(gen.activeSeconds / 60)} minutes of work</p>}
    </article>
  );
}

type Mode = 'simple' | 'custom';

const ANSWERS = ['Tap', 'Drag', 'Put in order', 'Match pairs', 'Sort into groups', 'Hold', 'Draw a path', 'Build'];
const CONTENT = ['Words', 'Pictures', 'Sounds', 'Numbers', 'The learner’s own photos'];

export function cleanDescription(text: string, names: { name: string; as: string }[]): { text: string; removed: number } {
  let removed = 0;
  let out = text;
  const swap = (re: RegExp, by: string) => { out = out.replace(re, () => { removed++; return by; }); };
  swap(/[\w.+-]+@[\w-]+\.[\w.-]+/g, '[email removed]');
  swap(/\b(?:\+?44\s?|0)(?:\d\s?){9,10}\b/g, '[phone removed]');
  swap(/\b[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}\b/gi, '[postcode removed]');
  swap(/\b\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4}\b/g, '[date removed]');
  swap(/\b\d{6,}\b/g, '[number removed]');
  const longestFirst = names.map((n) => ({ name: n.name.trim(), as: n.as })).filter((n) => n.name.length > 1)
    .filter((n) => !n.as.toLowerCase().split(/\s+/).includes(n.name.toLowerCase()))
    .sort((a, b) => b.name.length - a.name.length);
  for (const n of longestFirst) {
    swap(new RegExp(`\\b${n.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi'), n.as);
  }
  return { text: out, removed };
}

function TypeCard({ type, onDeleted }: { type: StudioType; onDeleted?: () => void }) {
  const nav = useNavigate();
  const { learnerId } = useLearner();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(type.manifest.name.replace(/\s*\(.*\)$/, ''));
  const [config, setConfig] = useState<Record<string, unknown>>({ studioType: type.manifest.id });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function make() {
    setBusy(true);
    setError(null);
    try {
      const p = await api<{ id: string }>(learnerId ? `/programs?learnerId=${encodeURIComponent(learnerId)}` : '/programs', {
        method: 'POST',
        token: true,
        body: { name: name.trim() || type.manifest.name, kind: 'studio', config: { ...config, studioType: type.manifest.id } },
      });
      nav(`/play/${p.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not make the program.');
      setBusy(false);
    }
  }

  async function remove() {
    if (!window.confirm(`Delete "${type.manifest.name}"? Programs made from it will stop working.`)) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/studio/types/${encodeURIComponent(type.manifest.id)}`, { method: 'DELETE', token: true });
      onDeleted?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete it.');
      setBusy(false);
    }
  }

  return (
    <article className="list-item studio-type">
      <div className="studio-type-head">
        <div>
          <p className="name" style={{ margin: 0 }}>{type.manifest.name}</p>
          <p className="field-hint" style={{ margin: 0 }}>{type.manifest.describes}</p>
        </div>
        <div className="toolbar">
          {onDeleted && (
            <button type="button" className="btn quiet-danger" disabled={busy} onClick={() => void remove()}>Delete</button>
          )}
          <button type="button" className="btn secondary" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
            {open ? 'Close' : 'Make a program'}
          </button>
        </div>
      </div>
      {error && !open && <div className="error" role="alert">{error}</div>}
      {open && (
        <div className="studio-type-form">
          {error && <div className="error" role="alert">{error}</div>}
          <div className="field">
            <label htmlFor={`n-${type.manifest.id}`}>Program name</label>
            <input id={`n-${type.manifest.id}`} value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
          </div>
          <StudioFields manifest={type.manifest} config={config} onChange={setConfig} />
          <div className="form-actions">
            <button type="button" className="btn" disabled={busy} onClick={() => void make()}>Make it and play</button>
          </div>
        </div>
      )}
    </article>
  );
}

export default function StudioPage() {
  const { access: ai } = useAiAccess();
  const aiVisible = !!ai?.visible;
  const { learners } = useLearner();
  const { user } = useAuth();
  const [mode, setMode] = useState<Mode>('simple');
  const [description, setDescription] = useState('');
  const [answers, setAnswers] = useState<string[]>([]);
  const [answerOther, setAnswerOther] = useState('');
  const [content, setContent] = useState<string[]>([]);
  const [contentOther, setContentOther] = useState('');
  const [settingsText, setSettingsText] = useState('');
  const [rules, setRules] = useState('');
  const [correct, setCorrect] = useState('');
  const [refs, setRefs] = useState<string[]>([]);

  const brief = useMemo(() => {
    if (mode === 'simple') return description;
    const how = [...answers, answerOther.trim()].filter(Boolean);
    const uses = [...content, contentOther.trim()].filter(Boolean);
    return [
      description.trim(),
      how.length > 0 && `How the learner answers: ${how.join(', ')}.`,
      uses.length > 0 && `What it uses: ${uses.join(', ')}.`,
      settingsText.trim() && `Settings teachers can change: ${settingsText.trim()}`,
      rules.trim() && `Rules: ${rules.trim()}`,
      correct.trim() && `A correct answer is: ${correct.trim()}`,
    ].filter(Boolean).join('\n');
  }, [mode, description, answers, answerOther, content, contentOther, settingsText, rules, correct]);
  const toggle = (set: (f: (cur: string[]) => string[]) => void, v: string) => set((cur) => (cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]));
  const names = useMemo(() => {
    const adult = (user?.displayName ?? '').trim();
    return [
      ...learners.map((l) => ({ name: l.name, as: 'the learner' })),
      ...(adult ? [adult, ...adult.split(/\s+/)].map((n) => ({ name: n, as: 'the adult' })) : []),
    ];
  }, [learners, user?.displayName]);
  const clean = useCallback((text: string) => cleanDescription(text, names), [names]);
  const cleaned = useMemo(() => clean(brief), [clean, brief]);

  const [gens, setGens] = useState<Generation[]>([]);
  const [current, setCurrent] = useState<Generation | null>(null);
  const [types, setTypes] = useState<StudioType[]>([]);
  const refGames = useMemo<RefGame[]>(() => [
    ...types.map((t) => ({ ref: `studio:${t.manifest.id}`, name: t.manifest.name, group: 'Your Studio game' })),
    ...CONCEPTS.map((c) => ({ ref: `game:${c.id}`, name: c.label, group: c.category })),
  ], [types]);
  const named = useMemo(() => namedIn(brief, refGames), [brief, refGames]);
  const used = useMemo(() => {
    const all = [...(mode === 'custom' ? refs : []), ...named.map((g) => g.ref)];
    return all.filter((r, i) => all.indexOf(r) === i).slice(0, 8);
  }, [mode, refs, named]);
  const unknownGame = used.length === 0 && VAGUE.some((re) => re.test(brief));
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [photo, setPhoto] = useState<PreparedPhoto | null>(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [noPeople, setNoPeople] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);

  async function choosePhoto(file: File | undefined) {
    setPhoto(null);
    setNoPeople(false);
    setPhotoError(null);
    if (!file) return;
    setPhotoBusy(true);
    try {
      const { preparePhoto } = await import('../studio/photo');
      setPhoto(await preparePhoto(file));
    } catch (e) {
      setPhotoError(e instanceof Error ? e.message : 'This photo could not be used.');
    } finally {
      setPhotoBusy(false);
    }
  }

  const refresh = useCallback(async () => {
    const [list, saved] = await Promise.all([
      api<Generation[]>('/studio/generations', { token: true }),
      listStudioTypes(),
    ]);
    setGens(list);
    setTypes(saved);
    return list;
  }, []);

  const open = useCallback(async (id: string) => {
    setCurrent(await api<Generation>(`/studio/generations/${id}`, { token: true }));
  }, []);

  useEffect(() => {
    if (!aiVisible) return;
    void refresh().then((list) => { if (list[0]) void open(list[0].id); }).catch(() => {});
  }, [aiVisible, refresh, open]);

  const busy = current?.status === 'queued' || current?.status === 'running';
  useEffect(() => {
    if (!current || !busy) return;
    const t = setInterval(() => {
      void api<Generation>(`/studio/generations/${current.id}`, { token: true }).then((g) => {
        setCurrent(g);
        if (g.status !== 'queued' && g.status !== 'running') { void refresh(); refreshAiAccess(); }
      }).catch(() => {});
    }, 5000);
    return () => clearInterval(t);
  }, [current, busy, refresh]);

  async function create() {
    setCreating(true);
    setCreateError(null);
    try {
      const r = await api<{ id: string }>('/studio/generations', { method: 'POST', token: true, body: { description: brief.trim() || 'Make a learning game from this photo.', photo: photo?.base64 ?? null, references: used } });
      setPhoto(null);
      setRefs([]);
      await refresh();
      await open(r.id);
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : 'Could not start it.');
    } finally {
      setCreating(false);
    }
  }

  if (!aiVisible) {
    return (
      <div className="card">
        <h1>Studio</h1>
        <p className="muted">Studio is not available on this account yet.</p>
      </div>
    );
  }

  if (ai && !ai.termsAccepted) return <AiTermsGate access={ai} />;
  return (
    <div className="studio">
      <div className="card">
        <h1 style={{ marginTop: 0 }}>Studio</h1>
        <p style={{ marginTop: 0 }}>Describe a new type of game and EasyLearning AI builds it, tests it and adds it to your programs.</p>
        <CreditsBar access={ai} />

        <div className="tabs" role="tablist" aria-label="How to describe it">
          <button type="button" role="tab" aria-selected={mode === 'simple'} className={`tab${mode === 'simple' ? ' is-active' : ''}`} onClick={() => setMode('simple')}>Simple</button>
          <button type="button" role="tab" aria-selected={mode === 'custom'} className={`tab${mode === 'custom' ? ' is-active' : ''}`} onClick={() => setMode('custom')}>Custom</button>
        </div>

        <div className="field">
          <label htmlFor="studio-desc">Describe the game you want</label>
          <textarea id="studio-desc" rows={5} maxLength={2000} value={description}
            placeholder="Anything you can imagine. e.g. The learner drags the right number of coins into a jar."
            onChange={(e) => setDescription(e.target.value)} />
          <p className="field-hint">Describe the game, not the learner. It does not have to be like any game EasyLearning has already.</p>
        </div>

        <div className="field studio-photo">
          <label htmlFor="studio-photo">Start from a photo (optional)</label>
          <input id="studio-photo" type="file" accept="image/*" onChange={(e) => void choosePhoto(e.target.files?.[0])} />
          <p className="field-hint">A photo of objects, pictures or a worksheet. Photos with a face are refused, and the photo's location and camera details are removed before anything is sent.</p>
          {photoBusy && <p role="status" className="muted">Checking the photo</p>}
          {photoError && <p className="error" role="alert">{photoError}</p>}
          {photo && (
            <div className="studio-photo-preview">
              <img src={photo.preview} alt="The photo that will be sent" />
              <button type="button" className="btn secondary" onClick={() => setPhoto(null)}>Remove photo</button>
            </div>
          )}
          {photo && (
            <label className="studio-photo-check">
              <input type="checkbox" checked={noPeople} onChange={(e) => setNoPeople(e.target.checked)} />
              <span>I have checked this photo: it shows no people, no names and nothing personal.</span>
            </label>
          )}
        </div>

        {mode === 'custom' && (
          <>
            <p className="field-hint" style={{ marginTop: 0 }}>Everything below is optional. Leave it empty and Studio decides.</p>
            <fieldset className="sub-group">
              <legend className="field-label">How the learner answers</legend>
              <div className="sub-cycles">
                {ANSWERS.map((a) => (
                  <label key={a} className={`sub-cycle${answers.includes(a) ? ' is-selected' : ''}`}>
                    <input type="checkbox" checked={answers.includes(a)} onChange={() => toggle(setAnswers, a)} />
                    <span>{a}</span>
                  </label>
                ))}
              </div>
              <div className="field" style={{ margin: 0 }}><input aria-label="Another way the learner answers" value={answerOther} maxLength={200}
                placeholder="Something else, e.g. tilts the phone, claps, traces a letter" onChange={(e) => setAnswerOther(e.target.value)} /></div>
            </fieldset>
            <fieldset className="sub-group">
              <legend className="field-label">What it uses</legend>
              <div className="sub-cycles">
                {CONTENT.map((c) => (
                  <label key={c} className={`sub-cycle${content.includes(c) ? ' is-selected' : ''}`}>
                    <input type="checkbox" checked={content.includes(c)} onChange={() => toggle(setContent, c)} />
                    <span>{c}</span>
                  </label>
                ))}
              </div>
              <div className="field" style={{ margin: 0 }}><input aria-label="Something else it uses" value={contentOther} maxLength={200}
                placeholder="Something else, e.g. a clock face, a map, music notes" onChange={(e) => setContentOther(e.target.value)} /></div>
              <p className="field-hint">Each becomes something teachers fill in when they make a program.</p>
            </fieldset>
            <ReferencePicker games={refGames} chosen={refs} onChange={setRefs} />
            <div className="field">
              <label htmlFor="studio-settings">Settings teachers can change</label>
              <input id="studio-settings" value={settingsText} maxLength={300} placeholder="e.g. how many choices, time limit, number of levels" onChange={(e) => setSettingsText(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="studio-rules">Rules</label>
              <input id="studio-rules" value={rules} maxLength={300} placeholder="e.g. at least 3 items, the answer is always one of the choices" onChange={(e) => setRules(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="studio-correct">What counts as a correct answer</label>
              <input id="studio-correct" value={correct} maxLength={300} placeholder="e.g. the jar holds exactly the number asked for" onChange={(e) => setCorrect(e.target.value)} />
            </div>
          </>
        )}

        {brief.trim() && (
          <div className="studio-sent">
            <p className="field-label" style={{ marginBottom: 4 }}>This is exactly what would be sent</p>
            <pre className="studio-sent-text">{cleaned.text}</pre>
            {cleaned.removed > 0 && <p className="field-hint">{cleaned.removed} personal detail{cleaned.removed === 1 ? ' was' : 's were'} removed.</p>}
            {used.length > 0 && <p className="field-hint">With these games as reference: {used.map((r) => refGames.find((g) => g.ref === r)?.name ?? r).join(', ')}.</p>}
          </div>
        )}
        {unknownGame && (
          <p className="error" role="alert">Studio cannot tell which game you mean. Choose it under Custom, in “Games to use as reference”, or describe the game itself.</p>
        )}

        <div className="form-actions">
          <button type="button" className="btn" disabled={creating || busy || photoBusy || unknownGame || (!brief.trim() && !photo) || (!!photo && !noPeople)} onClick={() => void create()}>Create</button>
        </div>
        {busy && <p className="field-hint">A game is being made. You can make another when it is finished.</p>}
        {createError && <p className="error" role="alert">{createError}</p>}

        <details className="studio-data">
          <summary>What is sent, and what never is</summary>
          <p><strong>Sent:</strong> only the description above and your replies to Studio, after names, email addresses, phone numbers, postcodes and dates are removed. While the game is built and tested, also its own code, test results and pictures of it running with example content it made up.</p>
          <p><strong>Never sent:</strong> anything about your learners (names, progress, answers, reports, photos, recordings, Talk Board) or about you (name, email, school, payment details, location). Your programs and word lists stay in EasyLearning.</p>
          <p><strong>How it is enforced:</strong> the request is built from a fixed list of fields with no room for anything else, requests go from our server so the AI service never sees your device, games run sealed off with no network access, and every request is logged so you can see exactly what was sent.</p>
        </details>
      </div>

      {current && (
        <div className="card">
          <GenerationCard gen={current} onSaved={() => void refresh().then(() => open(current.id))}
            clean={clean} blocked={gens.some((g) => g.status === 'queued' || g.status === 'running')}
            onRevised={(id) => void refresh().then(() => open(id))}
            onChanged={(g) => { setCurrent(g); void refresh(); }}
            parentName={gens.find((g) => g.id === current.revisionOf)?.manifest?.name ?? null} />
          {gens.length > 1 && (
            <div className="studio-history">
              <label htmlFor="studio-history" className="field-label">Earlier requests</label>
              <select id="studio-history" value={current.id} onChange={(e) => void open(e.target.value)}>
                {gens.map((g) => (
                  <option key={g.id} value={g.id}>
                    {new Date(g.createdAt).toLocaleString()} · {g.manifest?.name ?? g.brief.slice(0, 40)} · {g.status}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {types.length > 0 && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>Studio game types</h2>
          <div className="list">
            {types.map((t) => <TypeCard key={t.manifest.id} type={t} onDeleted={() => void refresh().then(() => current && open(current.id))} />)}
          </div>
        </div>
      )}
    </div>
  );
}
